import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

export interface NotificationPreferences {
  newOrders: boolean;
  lowStockAlerts: boolean;
  dailyReports: boolean;
  marketingUpdates: boolean;
}

const DEFAULT_PREFERENCES: NotificationPreferences = {
  newOrders: true,
  lowStockAlerts: true,
  dailyReports: false,
  marketingUpdates: false,
};

interface UserSettings {
  notifications?: Partial<NotificationPreferences>;
  feature_flags?: Record<string, boolean>;
  appearance?: {
    theme?: 'light' | 'dark' | 'system';
  };
  [key: string]: unknown;
}

export function useNotificationPreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch user settings from database
  const { data: userSettings, isLoading } = useQuery({
    queryKey: ['user-settings', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      return (data?.settings as UserSettings) || {};
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Merge defaults with stored preferences
  const preferences: NotificationPreferences = {
    ...DEFAULT_PREFERENCES,
    ...userSettings?.notifications,
  };

  // Mutation to save preferences to database
  const savePreferences = useMutation({
    mutationFn: async (newPreferences: NotificationPreferences) => {
      if (!user?.id) throw new Error('User not authenticated');

      // First get current settings to preserve other data
      const { data: currentData } = await supabase
        .from('profiles')
        .select('settings')
        .eq('id', user.id)
        .single();

      const currentSettings = (currentData?.settings as UserSettings) || {};
      const newSettings: UserSettings = {
        ...currentSettings,
        notifications: newPreferences,
      };

      const { error } = await supabase
        .from('profiles')
        .update({ settings: newSettings })
        .eq('id', user.id);

      if (error) throw error;
      return newPreferences;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings', user?.id] });
    },
  });

  const setPreference = useCallback(
    (key: keyof NotificationPreferences, value: boolean) => {
      const newPreferences = { ...preferences, [key]: value };
      savePreferences.mutate(newPreferences);
    },
    [preferences, savePreferences]
  );

  const setAllPreferences = useCallback(
    (newPreferences: Partial<NotificationPreferences>) => {
      const merged = { ...preferences, ...newPreferences };
      savePreferences.mutate(merged);
    },
    [preferences, savePreferences]
  );

  return {
    preferences,
    isLoading,
    isSaving: savePreferences.isPending,
    setPreference,
    setAllPreferences,
    error: savePreferences.error,
  };
}















